/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, ReactNode, useContext } from "react"
import { observer } from "mobx-react-lite"
import moment from "moment"
import VirtualExamContext from "views/exam/virtual-exam/virtual-exam-context"

/**
 * The booked patient's name, date of birth and phone number, for the technician assigned to a booked exam. The phone
 * number is a link, so a device that can make calls can call a patient who has not joined. As one line under the
 * video, or stacked, one field per line, where the technician confirms the patient's identity.
 */
const BookedPatientDetails = observer(({ stacked = false }: { stacked?: boolean }): ReactElement | null => {
  const { virtualExamStore } = useContext(VirtualExamContext)
  const bookedPatient = virtualExamStore.bookedPatient
  if (!bookedPatient) {
    return null
  }
  const fields: [string, ReactNode][] = [
    [stacked ? "Name" : "Patient", bookedPatient.fullName],
    ["Date of birth", bookedPatient.birthdate ? moment(bookedPatient.birthdate).format("ll") : "Not on record"],
    [
      "Phone",
      bookedPatient.phoneNumber ? (
        <a className="link" href={`tel:${bookedPatient.phoneNumber.replace(/[^\d+]/g, "")}`}>
          {bookedPatient.phoneNumber}
        </a>
      ) : (
        "No phone number on record"
      ),
    ],
  ]
  return (
    <div
      className={stacked ? "space-y-1" : "flex flex-wrap justify-center gap-x-6"}
      data-testid="booked-patient-details"
    >
      {fields.map(([label, value]) => (
        <div key={label}>
          <span className="font-semibold">{label}:</span> {value}
        </div>
      ))}
    </div>
  )
})

export default BookedPatientDetails
